import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { ExternalLink, FileText, BookOpen, Scale, Download } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { DEMO_AIRCRAFT } from '../../lib/demoData';
import type { AircraftDoc } from '../../lib/types';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { PageHeader } from '../../components/PageHeader';
import { TRACKED_REG } from '../../lib/flightTrack';
import { OWNER_FIRST } from '../../lib/identity';

// Self-serve avionics familiarization hub for N8366X. The panel is an all-glass,
// vacuum-free Garmin suite (IFR / Technically Advanced Aircraft). Each unit links
// to the manufacturer's product page and pilot's guide so a lessee can study up
// before their checkout / IFR work. Confirmed against the panel photo + logbook.
interface Avionic {
  img: string;
  name: string;
  tag: string;
  blurb: string;
  productUrl: string;
  guideUrl?: string;
}

const AVIONICS: Avionic[] = [
  {
    img: '/avionics/g5.jpg',
    name: 'Dual Garmin G5',
    tag: 'Primary flight displays',
    blurb:
      'Two G5 electronic flight instruments — one as the attitude indicator (PFD), one as the HSI. Bright, sunlight-readable glass with their own backup batteries. This is your primary attitude and heading reference.',
    productUrl: 'https://www.garmin.com/en-US/p/570665/',
    guideUrl: 'https://static.garmin.com/pumac/190-01112-12_j.pdf',
  },
  {
    img: '/avionics/gfc500.jpg',
    name: 'Garmin GFC 500',
    tag: 'Digital autopilot',
    blurb:
      'Two-axis digital autopilot and flight director driven by the GMC 507 mode controller in the center stack. Altitude hold/preselect, heading, NAV/APR coupling, VNAV, level button, and Electronic Stability & Protection (ESP). Great for single-pilot IFR.',
    productUrl: 'https://www.garmin.com/en-US/p/604257/',
    guideUrl: 'https://static.garmin.com/pumac/190-02291-07_01.pdf',
  },
  {
    img: '/avionics/gnx375.jpg',
    name: 'Garmin GNX 375',
    tag: 'GPS navigator + ADS-B transponder',
    blurb:
      'Touchscreen WAAS GPS navigator with a full-color moving map and LPV approach capability, plus a built-in Mode S transponder with ADS-B In/Out — so you get traffic and weather, and you are 2020-mandate compliant.',
    productUrl: 'https://www.garmin.com/en-US/p/577174/',
    guideUrl: 'https://static.garmin.com/pumac/190-02488-01_b.pdf',
  },
  {
    img: '/avionics/gnc255.jpg',
    name: 'Garmin GNC 255A',
    tag: 'Nav / comm radio',
    blurb:
      'Slimline VHF comm transceiver paired with a 200-channel VOR / localizer / glideslope nav receiver — your backup comm and ground-based nav (ILS / VOR approaches).',
    productUrl: 'https://www.garmin.com/en-US/p/102764/',
    guideUrl: 'https://static.garmin.com/pumac/190-01182-01_e.pdf',
  },
  {
    img: '/avionics/gma345.jpg',
    name: 'Garmin GMA 345',
    tag: 'Audio panel',
    blurb:
      'Digital audio panel with a six-place stereo intercom, Bluetooth (take calls or stream audio), a USB charging port, and a built-in clearance recorder that plays back the last ~60 seconds of a comm transmission.',
    productUrl: 'https://www.garmin.com/en-US/p/506609/',
    guideUrl: 'https://static.garmin.com/pumac/190-01878-01_d.pdf',
  },
  {
    img: '/avionics/edm900.jpg',
    name: 'JPI EDM 900',
    tag: 'Primary engine monitor',
    blurb:
      'TSO/STC primary engine monitor — per-cylinder EGT/CHT, fuel flow, and full engine data three times a second, with limit warnings. Use it to lean properly and keep the Continental O-300 healthy.',
    productUrl: 'https://www.jpinstruments.com/shop/edm-900-2/',
  },
];

function LinkChip({
  href,
  icon: Icon,
  children,
}: {
  href: string;
  icon: LucideIcon;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-navy-700 transition-colors hover:border-brand hover:bg-slate-50"
    >
      <Icon size={14} />
      {children}
      <ExternalLink size={11} className="text-ink-subtle" />
    </a>
  );
}

/**
 * Empty weight, CG and the certificated envelope, on the page a pilot already
 * opens to read about the airplane. These lived only on a sheet in the aircraft,
 * which is no help when you're planning a flight the night before.
 */
function WeightAndBalance() {
  const { demoMode } = useAuth();
  const [ac, setAc] = useState<AircraftDoc | null>(demoMode ? DEMO_AIRCRAFT[0] : null);
  useEffect(() => {
    if (demoMode || !db) return;
    return onSnapshot(doc(db, 'aircraft', TRACKED_REG), (s) =>
      setAc(s.exists() ? ({ ...(s.data() as AircraftDoc), aircraftId: s.id }) : null)
    );
  }, [demoMode]);

  const wb = ac?.weightBalance;
  const v = ac?.tcds?.vSpeedsKcas;
  const env = ac?.tcds?.cgEnvelope?.normal;
  const st = ac?.tcds?.stations;
  const cap = ac?.tcds?.capacities;
  const ff = ac?.foreflightProfile;
  if (!wb) return null;

  return (
    <div className="card mb-4 p-5">
      <div className="flex items-center gap-2">
        <Scale size={17} className="text-navy-700" />
        <h2 className="font-display text-base font-semibold text-navy-900">Weight &amp; balance</h2>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-[11px] uppercase tracking-[0.07em] text-ink-subtle">Empty weight</dt>
          <dd className="font-display text-lg font-semibold text-navy-900">{wb.emptyWeight} lb</dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-[0.07em] text-ink-subtle">Empty CG</dt>
          <dd className="font-display text-lg font-semibold text-navy-900">{wb.emptyCg} in</dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-[0.07em] text-ink-subtle">Useful load</dt>
          <dd className="font-display text-lg font-semibold text-navy-900">{wb.usefulLoad} lb</dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-[0.07em] text-ink-subtle">Max gross</dt>
          <dd className="font-display text-lg font-semibold text-navy-900">{wb.maxGrossWeight} lb</dd>
        </div>
      </dl>

      {env && env.length > 0 && (
        <p className="mt-3 border-t border-slate-100 pt-3 text-sm text-ink">
          <span className="font-semibold">Normal-category CG range:</span>{' '}
          {env.map((p) => `${p.fwd}–${p.aft} in at ${p.weight} lb`).join(' · ')} — straight-line
          variation between those points.
        </p>
      )}
      {v && (
        <p className="mt-2 text-sm text-ink">
          <span className="font-semibold">V-speeds (CAS):</span> Va {v.maneuvering} kt · Vno{' '}
          {v.maxStructuralCruise} kt · Vne {v.neverExceed} kt · Vfe {v.flapsExtended} kt
        </p>
      )}
      {st && cap && (
        <p className="mt-2 text-sm text-ink">
          <span className="font-semibold">Stations:</span> front seats +{st.frontSeats}&Prime; · rear
          seats +{st.rearSeats}&Prime; · baggage +{st.baggage}&Prime; (max {cap.maxBaggageLb} lb) ·
          fuel +{st.fuel}&Prime; ({cap.fuelGalUsable} gal usable of {cap.fuelGalTotal}) · oil{' '}
          {st.oil}&Prime;
        </p>
      )}
      {ff && (
        <a
          href={ff.url}
          target="_blank"
          rel="noopener"
          className="mt-4 flex items-center gap-3 rounded-xl border border-slate-200 p-3 transition hover:bg-sky-50"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-navy-700">
            <Download size={18} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-medium text-navy-900">ForeFlight weight &amp; balance profile</span>
            <span className="block text-xs text-ink-muted">
              {ff.note ?? `${TRACKED_REG}, already built. Tap on your iPad and ForeFlight will ask to import it — you'll need the aircraft added in ForeFlight first.`}
            </span>
          </span>
        </a>
      )}

      <p className="mt-3 text-xs text-ink-muted">
        Empty weight and CG from the last weighing{wb.weighedBy ? ` — ${wb.weighedBy}` : ''}. Limits
        from the Type Certificate Data Sheet for the 172C. Work your own weight and balance for every
        flight; this is reference, not a substitute for the numbers in the aircraft.
      </p>
    </div>
  );
}

export function Resources() {
  return (
    <>
      <PageHeader
        title="Aircraft & avionics"
        subtitle={`Get to know ${TRACKED_REG} — an IFR-rated, all-glass Technically Advanced Aircraft.`}
      />

      <div className="card mb-4 p-5">
        <h2 className="font-display text-base font-semibold text-navy-900">The panel</h2>
        <p className="mt-2 text-sm text-ink">
          {TRACKED_REG} is a 1961 Cessna 172C powered by a Continental O-300-D, with a modern,
          vacuum-free, all-electric Garmin glass panel. It&apos;s a genuine IFR platform and a
          Technically Advanced Aircraft (TAA): IFR-approved GPS, a moving-map display, and a digital
          autopilot. Tap any unit below to read the manufacturer&apos;s product page or the full
          pilot&apos;s guide — worth a look before your checkout or any IFR work.
        </p>
      </div>

      <WeightAndBalance />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {AVIONICS.map((a) => (
          <div key={a.name} className="card flex flex-col overflow-hidden p-0">
            <div className="flex h-36 items-center justify-center border-b border-slate-100 bg-slate-50 p-4">
              <img
                src={a.img}
                alt={a.name}
                loading="lazy"
                className="max-h-full max-w-full object-contain"
              />
            </div>
            <div className="flex flex-1 flex-col p-5">
              <h3 className="font-display text-base font-semibold leading-tight text-navy-900">
                {a.name}
              </h3>
              <div className="text-xs font-medium text-ink-subtle">{a.tag}</div>
              <p className="mt-3 flex-1 text-sm text-ink">{a.blurb}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <LinkChip href={a.productUrl} icon={BookOpen}>
                  Learn more
                </LinkChip>
                {a.guideUrl && (
                  <LinkChip href={a.guideUrl} icon={FileText}>
                    Pilot&apos;s guide
                  </LinkChip>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <p className="mt-5 rounded-lg bg-sky-50 px-4 py-3 text-xs text-ink-muted">
        There is a flight log binder in the airplane. It is mandatory and it stays in the airplane — Log a
        Flight in this app is the record. It&apos;s there for the day the app won&apos;t load or you have no
        signal: write the numbers in the binder, text {OWNER_FIRST}, and log it here when you&apos;re back on. The
        binder also holds the VOR check sheet, which is where you log yours before filing IFR.
      </p>

      <p className="mt-3 rounded-lg bg-sky-50 px-4 py-3 text-xs text-ink-muted">
        Links go to Garmin and J.P. Instruments. Always fly the aircraft by its installed flight
        manual supplements (AFMS) and placards — manufacturer guides are for familiarization, not a
        substitute for the approved documentation in the airplane.
      </p>
    </>
  );
}
